import React, { useEffect, useRef } from 'react';
import {
  Button,
  ButtonType,
  Modal,
  Row,
  Select
} from 'syngenta-digital-cropwise-react-ui-kit';
import ChevronUp from 'assets/chevron_up.svg?react';
import ChevronDown from 'assets/chevron_down.svg?react';
import DownloadIcon from 'assets/download_outlined_green.svg?react';
import PrintIcon from 'assets/print.svg?react';
import { OrientationOptions } from 'screens/Applications&WorkOrders/Hooks/useOperationPdf';

interface PdfSettingsModalProps {
  open: boolean;
  orientation: OrientationOptions;
  onOrientationChange: (orientation: OrientationOptions) => void;
  onDownload: () => void;
  onPrint: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const PdfSettingsModal: React.FC<PdfSettingsModalProps> = ({
  open,
  orientation,
  onOrientationChange,
  onDownload,
  onPrint,
  onCancel,
  isLoading = false
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const printButtonWrapperRef = useRef<HTMLDivElement>(null);

  const chevronIcon = isDropdownOpen ? (
    <ChevronUp width={18} height={18} />
  ) : (
    <ChevronDown width={18} height={18} />
  );

  const handleDropdownVisibleChange = (dropdownOpen: boolean) => {
    setIsDropdownOpen(dropdownOpen);
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (open && printButtonWrapperRef.current) {
      // Small delay to ensure the modal is fully rendered
      timeoutId = setTimeout(() => {
        const button = printButtonWrapperRef.current?.querySelector('button');
        if (button) {
          button.focus();
        }
      }, 100);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [open]);

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      centered
      width={423}
      maskClosable={false}
      keyboard={false}
      destroyOnClose={false}
      zIndex={1050}
    >
      <div className="text-base font-semibold mb-2">PDF settings</div>
      <label
        htmlFor="orientation-select"
        className="text-sm font-normal text-neutral-60"
      >
        Layout
      </label>
      <Select
        id="orientation-select"
        data-testid="orientation-select"
        options={[
          { label: 'Portrait', value: 'portrait' },
          { label: 'Landscape', value: 'landscape' }
        ]}
        className="w-full mb-4"
        value={orientation}
        onChange={onOrientationChange}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        suffixIcon={chevronIcon}
      />
      <Row justify="space-between" className="pt-6">
        <Button
          type={ButtonType.outline}
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <div className="flex-grow" />
        <Button
          type={ButtonType.primary}
          ghost
          className="px-3 mr-2"
          icon={<DownloadIcon />}
          onClick={onDownload}
          disabled={isLoading}
        >
          Download
        </Button>
        <div ref={printButtonWrapperRef}>
          <Button
            type={ButtonType.primary}
            className="px-3"
            icon={<PrintIcon />}
            onClick={onPrint}
            disabled={isLoading}
          >
            Print
          </Button>
        </div>
      </Row>
    </Modal>
  );
};

export default PdfSettingsModal;
